import React, { useState } from "react";

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { Confirm, useNotify, useRefresh } from "react-admin";

import { DeleteIcon } from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import { InvoiceStatus, ResourceType } from "../../../utils/constants";
import { formatCurrency } from "../../../utils/utils";

import { customLogic } from "./customLogic";
import { formatDate } from "./InvoiceUtils";
import {
  describePayment,
  getBalance,
  getPaymentMethodName,
  planRemovePayment,
  sumPayments,
} from "./invoicePaymentUtils";
import { CONFLICT_MESSAGE, saveInvoiceChange } from "./saveInvoiceChange";

const SKIP_MESSAGES = {
  missing: "This payment was already deleted.",
  void: "The invoice is void; payments can no longer be deleted.",
  conflict: CONFLICT_MESSAGE,
};

/**
 * Payments recorded on the invoice, read-only, with delete for mistakes.
 * Nothing here is a form input, so the invoice Save never sends payments.
 */
export const InvoicePaymentsPanel = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!record) return null;

  const payments = (record.payments || []).filter(Boolean);
  const paid = sumPayments(payments);
  const balance =
    record.status === InvoiceStatus.PAID ? 0 : getBalance(record.total, paid);
  const canDelete =
    record.status !== InvoiceStatus.VOID && !customLogic() && !loading;

  const handleDelete = async () => {
    const payment = deleting;
    if (!payment) return;
    setLoading(true);
    try {
      // Works on the invoice as read at delete time, not this page's copy, so
      // a payment saved elsewhere since the page loaded stays.
      const result = await saveInvoiceChange(record.id, (invoice) =>
        planRemovePayment(invoice, payment.id),
      );
      if (result.skip) {
        notify(SKIP_MESSAGES[result.skip], "warning");
        setDeleting(null);
        refresh();
        return;
      }

      const { invoice, update } = result;
      const reopened = update.status === InvoiceStatus.PENDING;
      logHistory({
        text: `Payment ${describePayment(payment)} deleted.${
          reopened ? " Invoice back to pending." : ""
        }`,
        parentId: invoice.id,
        parentType: ResourceType.INVOICE,
      });
      notify(
        reopened
          ? "Payment deleted. Invoice is pending again."
          : "Payment deleted.",
        "info",
      );
      setDeleting(null);
      refresh();
    } catch (error) {
      logError("invoices:InvoicePaymentsPanel:handleDelete", error, notify);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      {payments.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {record.status === InvoiceStatus.PENDING
            ? "No payments recorded. Use Record Payment from the Complete menu."
            : "No payments recorded. Payments can be recorded once the invoice is sent."}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Check No. / Reference</TableCell>
              <TableCell>Memo</TableCell>
              <TableCell>Recorded By</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                <TableCell align="right">
                  {formatCurrency(payment.amount)}
                </TableCell>
                <TableCell>{getPaymentMethodName(payment.method)}</TableCell>
                <TableCell>{payment.reference || ""}</TableCell>
                <TableCell>{payment.memo || ""}</TableCell>
                <TableCell>
                  {payment.recordedBy || ""}
                  {payment.recordedAt
                    ? ` · ${formatDate(payment.recordedAt)}`
                    : ""}
                </TableCell>
                <TableCell align="right" padding="checkbox">
                  <Tooltip title="Delete payment" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Delete payment"
                        disabled={!canDelete}
                        onClick={() => setDeleting(payment)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell align="right">
                <strong>Total</strong>
              </TableCell>
              <TableCell align="right">
                {formatCurrency(record.total)}
              </TableCell>
              <TableCell colSpan={5} />
            </TableRow>
            <TableRow>
              <TableCell align="right">
                <strong>Paid</strong>
              </TableCell>
              <TableCell align="right">{formatCurrency(paid)}</TableCell>
              <TableCell colSpan={5} />
            </TableRow>
            <TableRow>
              <TableCell align="right">
                <strong>Balance</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{formatCurrency(balance)}</strong>
              </TableCell>
              <TableCell colSpan={5} />
            </TableRow>
          </TableBody>
        </Table>
      )}
      <Confirm
        isOpen={Boolean(deleting)}
        loading={loading}
        title="Delete Payment"
        content={
          deleting
            ? `Delete the ${describePayment(deleting)} payment? The invoice balance is recalculated.`
            : ""
        }
        onConfirm={handleDelete}
        onClose={() => {
          if (!loading) setDeleting(null);
        }}
      />
    </div>
  );
};
